/**
 * Type definitions for AI/Story generation
 * 
 * This file contains ONLY type definitions with no imports of server-side code.
 * Safe to import in both client and server components.
 */

// Define our own Gender enum instead of importing from Prisma
export enum Gender {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
  OTHER = 'OTHER'
}

// Types for child details and theme
export interface ChildDetails {
  firstName: string;
  gender: Gender;
  age: number;
  interests?: string;
  hairColor?: string;
  hairStyle?: string;
  skinColor?: string;
  language?: string; // ISO 639-1 language code for story generation
  photoUrl?: string; // URL of the child's photo for character consistency
}

// Antagonist configuration for villain-type stories
export interface Antagonist {
  name?: string;
  description?: string;
  threat?: string;
}

// Conflict types that determine story dramatic arc guidance
export type ConflictType =
  | 'villain'       // Named villain stories (superhero, pirate, knight)
  | 'environmental' // Environmental challenge (space, ocean, mountain)
  | 'competition'   // Competition stories (sports, contests)
  | 'mystery'       // Mystery stories (detective, puzzle)
  | 'internal'      // Internal conflict (growth, overcoming fear)
  | 'discovery';    // Discovery stories (exploration, learning)

export interface StoryContext {
  culturalSetting?: string;
  culturalDetails?: string;
  // Conflict/dramatic arc configuration (from template)
  conflictType?: ConflictType;
  antagonist?: Antagonist;
}

export interface Style {
  id: string;
  name: string;
  description: string;
  previewImageUrl: string;
  isActive: boolean;
  experimental?: boolean; // Flag for experimental styles
}

export interface StoryPage {
  id: string;
  pageNumber: number;
  content: string;
  imageUrl: string;
  imagePrompt?: string;
  generatedWithoutText?: boolean; // Indicates if the image generated was without text and requires client-side overlay
}

// Image generation parameters interface
export interface ImageGenerationParams {
  width: number;
  height: number;
  aspectRatio: string;
  includeTitle?: boolean;
}
